import { index, pgEnum, pgTable, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { usStatesEnum, usStatesEnumSchema } from "./commonSchema";
import { npsSurveysTable } from "./npsSurveysSchema";
import { patientsTable } from "./patientsSchema";
import { customTimestamp } from "./utils/types";

export const applicationTypeEnumSchema = z.enum([
  "MEDICAID",
  "MEDICARE",
  "MARKETPLACE",
]);
export type ApplicationTypeEnumType = z.infer<typeof applicationTypeEnumSchema>;
export const applicationTypeEnum = pgEnum(
  "application_type_enum",
  applicationTypeEnumSchema.enum,
);

export const applicationStatusEnumSchema = z.enum(["DRAFT", "SUBMITTED"]);

export type ApplicationStatusEnumType = z.infer<
  typeof applicationStatusEnumSchema
>;

export const applicationStatusEnum = pgEnum(
  "application_status_enum",
  applicationStatusEnumSchema.enum,
);

export const applicationsTable = pgTable(
  "applications",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    patientId: uuid("patient_id")
      .references(() => patientsTable.id)
      .notNull(),
    applicationType: applicationTypeEnum("application_type").notNull(),
    applicationState: usStatesEnum("application_state").notNull(),
    applicationStatus: applicationStatusEnum("application_status")
      .notNull()
      .default("DRAFT"),
    npsSurveyId: uuid("nps_survey_id").references(() => npsSurveysTable.id, {
      onDelete: "set null",
    }),
    submittedAt: customTimestamp("submitted_at"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [index("applications_patient_id_index").on(table.patientId)],
);

export const insertApplicationSchema = createInsertSchema(applicationsTable, {
  patientId: z.uuid().min(1),
  applicationType: applicationTypeEnumSchema,
  applicationState: usStatesEnumSchema,
})
  .omit({
    createdAt: true,
    submittedAt: true,
    updatedAt: true,
  })
  .extend({
    applicationStatus: applicationStatusEnumSchema.default("DRAFT").optional(),
  });

export type InsertApplicationType = z.infer<typeof insertApplicationSchema>;

export const applicationSchema = createSelectSchema(applicationsTable, {
  applicationState: usStatesEnumSchema,
  applicationType: applicationTypeEnumSchema,
  applicationStatus: applicationStatusEnumSchema,
});
export type ApplicationType = z.infer<typeof applicationSchema>;

export const updateApplicationSchema = insertApplicationSchema
  .omit({
    patientId: true,
    applicationState: true,
    id: true,
    // Linked server-side after an NPS survey is submitted; not client-settable
    npsSurveyId: true,
  })
  .partial();

export type UpdateApplicationType = z.infer<typeof updateApplicationSchema>;
